import { useCallback, useEffect, useState } from 'react';
import { backendApi, ApiError } from '../lib/api';
import { StatCard, Icon } from '../components/Icon';
import { UsageChart, GrowthChart, type UsageDay, type GrowthDay } from '../components/Charts';

/**
 * White-label partner platform admin — backed by wpcursor-backend's
 * /api/license/admin/partners surface (module 'partners').
 *
 * Layout follows the Accounts module: rich rows in one table, expanding
 * INLINE (detail-row/detail-panel) per mode. Raw API keys and webhook
 * secrets are returned by create/mint/rotate EXACTLY once — the one-time
 * modal is the only place they ever render.
 */

interface Partner {
  id: string; name: string; slug: string; status: string;
  default_plan_id: string; max_accounts: number | null;
  webhook_url: string | null; webhookConfigured: boolean;
  created_at: string;
  account_count: string | number; site_count: string | number;
  key_count: string | number; job_count: string | number;
  active_job_count: string | number;
}
interface PartnerKey {
  id: string; key_prefix: string; name: string | null; scopes: string[];
  last_used_at: string | null; expires_at: string | null;
  is_active: boolean; created_at: string;
}
interface PartnerAccount {
  id: string; email: string; name: string | null; company: string | null;
  plan_id: string; status: string; credits_balance: number;
  partner_external_ref: string | null; created_at: string;
  site_count: string | number;
}
interface PartnerJob {
  id: string; site_id: string; status: string; model: string;
  credits_consumed: number | null; cost_usd: string | number | null;
  error: string | null; created_at: string; finished_at: string | null;
}
interface AuditRow {
  action: string; target_id: string | null; api_key_id: string | null;
  ip: string | null; payload: unknown; created_at: string;
}
interface Overview {
  partner: Partner & Record<string, unknown>;
  counts: { accounts: number; sites: number; keys: number };
  auditLog: AuditRow[];
}
interface Detail {
  overview: Overview; keys: PartnerKey[]; accounts: PartnerAccount[];
  jobs: PartnerJob[]; jobsTotal: number;
}
interface Dashboard {
  days: number;
  usage: UsageDay[];
  usageTotal: number;
  growth: GrowthDay[];
  jobsByDay: { date: string; total: number; succeeded: number; failed: number; credits: number; cost: number }[];
  totals: {
    jobs: { total: number; succeeded: number; failed: number; credits: number; cost: number; successRate: number | null };
    creditsWindow: number; creditsAllTime: number;
    lastKeyUsedAt: string | null;
    webhook: { delivered: number; failed: number; pending: number; lastDeliveredAt: string | null; lastError: string | null };
  };
  topAccounts: { email: string; company: string | null; credits: number; siteCount: number }[];
}

const ALL_SCOPES = ['provision', 'read', 'handoff', 'jobs', 'embed'];
const PLANS = ['free', 'standard', 'pro', 'agency'];
type Mode = 'overview' | 'keys' | 'accounts' | 'jobs' | 'audit';

const num = (v: string | number | null | undefined) => Number(v) || 0;

/** Human-readable failure — never dump a raw HTML error page into the UI. */
function friendlyError(err: ApiError): string {
  if (err.isForbidden) return 'You do not have access to partners.';
  // A 404 with a real JSON error body is a resource-level miss ("No account
  // with that email", "Partner not found") — show it. A bodyless 404 means the
  // endpoint itself isn't mounted on this tier — then, and only then, hint at
  // the env pill. (Previously every 404 showed the tier hint, so a typo'd owner
  // email read as a deployment problem.)
  if (err.status === 404 && !(err.body && (err.body.error || err.body.message))) {
    return 'The partner admin API is not on this backend tier yet — switch the env pill to Dev, or promote wpcursor-backend to main.';
  }
  const m = (err.message || 'Request failed').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return m.length > 160 ? `${m.slice(0, 160)}…` : m;
}

function statusBadge(status: string): string {
  if (status === 'active' || status === 'succeeded') return 'badge badge-green';
  if (status === 'queued' || status === 'running') return 'badge badge-amber';
  return 'badge badge-gray';
}

function auditActor(row: AuditRow): string {
  try {
    const payload = typeof row.payload === 'string' ? JSON.parse(row.payload) : (row.payload ?? {});
    const actor = (payload as { actor?: { name?: string; id?: string } }).actor;
    if (actor?.name || actor?.id) return actor.name ?? actor.id ?? '';
  } catch { /* machine-written payload */ }
  return row.api_key_id ? 'Partner API key' : 'system';
}

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const d = Date.now() - +new Date(iso);
  const days = Math.floor(d / 86400000);
  if (days > 30) return new Date(iso).toLocaleDateString();
  if (days >= 1) return `${days}d ago`;
  const hours = Math.floor(d / 3600000);
  if (hours >= 1) return `${hours}h ago`;
  const mins = Math.max(1, Math.floor(d / 60000));
  return `${mins}m ago`;
}

export default function Partners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const [open, setOpen] = useState<{ id: string; mode: Mode } | null>(null);
  const [details, setDetails] = useState<Record<string, Detail>>({});
  const [detailLoading, setDetailLoading] = useState(false);

  const [dashboards, setDashboards] = useState<Record<string, Dashboard>>({});
  const [dashDays, setDashDays] = useState<7 | 30 | 90>(30);
  const [createOpen, setCreateOpen] = useState(false);
  const [mintFor, setMintFor] = useState<string | null>(null);
  const [webhookFor, setWebhookFor] = useState<string | null>(null);
  const [ownerFor, setOwnerFor] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ title: string; value: string; note: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    backendApi.get<{ partners: Partner[] }>('/license/admin/partners')
      .then((d) => {
        setPartners(d.partners || []);
        setError('');
        // One partner → open it, nothing to choose.
        if ((d.partners || []).length === 1) {
          setOpen((cur) => cur ?? { id: d.partners[0].id, mode: 'overview' });
          void loadDetail(d.partners[0].id);
          void loadDashboard(d.partners[0].id, 30);
        }
      })
      .catch((err: ApiError) => setError(friendlyError(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      const [o, k, a, j] = await Promise.all([
        backendApi.get<Overview>(`/license/admin/partners/${id}`),
        backendApi.get<{ keys: PartnerKey[] }>(`/license/admin/partners/${id}/keys`).catch(() => ({ keys: [] })),
        backendApi.get<{ accounts: PartnerAccount[] }>(`/license/admin/partners/${id}/accounts`).catch(() => ({ accounts: [] })),
        backendApi.get<{ jobs: PartnerJob[]; total: number }>(`/license/admin/partners/${id}/jobs?limit=50`).catch(() => ({ jobs: [], total: 0 })),
      ]);
      setDetails((cur) => ({ ...cur, [id]: { overview: o, keys: k.keys || [], accounts: a.accounts || [], jobs: j.jobs || [], jobsTotal: j.total || 0 } }));
    } catch (err) {
      setError(friendlyError(err as ApiError));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadDashboard = useCallback(async (id: string, days: number) => {
    try {
      const d = await backendApi.get<Dashboard>(`/license/admin/partners/${id}/dashboard?days=${days}`);
      setDashboards((cur) => ({ ...cur, [`${id}:${days}`]: d }));
    } catch { /* panel shows its own loading/empty state */ }
  }, []);

  const toggle = (id: string, mode: Mode) => {
    const next = open && open.id === id && open.mode === mode ? null : { id, mode };
    setOpen(next);
    if (next && !details[next.id]) void loadDetail(next.id);
    if (next && next.mode === 'overview' && !dashboards[`${next.id}:${dashDays}`]) void loadDashboard(next.id, dashDays);
  };

  async function revokeKey(partnerId: string, key: PartnerKey) {
    if (!window.confirm(`Revoke key ${key.key_prefix}…? The partner loses access immediately. This cannot be undone.`)) return;
    try {
      await backendApi.delete(`/license/admin/partners/${partnerId}/keys/${key.id}`);
      setNote(`Key ${key.key_prefix}… revoked.`);
      void loadDetail(partnerId); void load();
    } catch (err) {
      setError(friendlyError(err as ApiError));
    }
  }

  const totals = {
    partners: partners.length,
    accounts: partners.reduce((n, p) => n + num(p.account_count), 0),
    keys: partners.reduce((n, p) => n + num(p.key_count), 0),
    activeJobs: partners.reduce((n, p) => n + num(p.active_job_count), 0),
  };

  return (
    <div>
      <header className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1>Partners</h1>
          <p className="muted">White-label partners — the accounts they manage, API keys, webhooks and build jobs.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>New Partner</button>
      </header>

      <div className="stat-row">
        <StatCard icon="users" tint="purple" value={totals.partners} label="Partners" />
        <StatCard icon="globe" tint="green" value={totals.accounts} label="Managed Accounts" />
        <StatCard icon="tag" tint="amber" value={totals.keys} label="Active Keys" />
        <StatCard icon="play" tint="purple" value={totals.activeJobs} label="Jobs Running" />
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {note && <div className="alert alert-ok">{note}</div>}

      <div className="card">
        {loading ? <p className="muted">Loading…</p> : partners.length === 0 ? (
          <p className="muted">No partners yet — create one to start white-labeling WPOS.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr>
                <th>Partner</th><th>Integration</th><th>Accounts</th><th>Jobs</th><th>Created</th><th style={{ textAlign: 'right' }}>Actions</th>
              </tr></thead>
              <tbody>
                {partners.map((row) => {
                  const d = details[row.id];
                  const activeKey = d?.keys.find((k) => k.is_active);
                  const isOpen = open?.id === row.id;
                  return (
                    <>
                      <tr key={row.id} style={isOpen ? { background: 'var(--color-surface-raised)' } : undefined}>
                        <td>
                          <div className="user-cell">
                            <div className="avatar">{(row.name || '?')[0].toUpperCase()}</div>
                            <div>
                              <div className="user-email">{row.name}</div>
                              <div className="user-meta">
                                <span className="mono">{row.slug}</span>{' '}
                                <span className={statusBadge(row.status)}>{row.status}</span>{' '}
                                <span className="badge badge-purple">{row.default_plan_id}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>{num(row.key_count)} key{num(row.key_count) === 1 ? '' : 's'}{activeKey?.last_used_at ? <span className="muted small"> · used {ago(activeKey.last_used_at)}</span> : ''}</div>
                          <div className="muted small">{row.webhookConfigured ? 'webhook configured' : 'no webhook'}</div>
                        </td>
                        <td>
                          <div>{num(row.account_count)}</div>
                          <div className="muted small">{num(row.site_count)} site{num(row.site_count) === 1 ? '' : 's'}</div>
                        </td>
                        <td>
                          <div>{num(row.job_count)}</div>
                          {num(row.active_job_count) > 0
                            ? <div><span className="badge badge-amber">{num(row.active_job_count)} running</span></div>
                            : <div className="muted small">none running</div>}
                        </td>
                        <td className="muted">{new Date(row.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn-icon" title="Overview" onClick={() => toggle(row.id, 'overview')}><Icon name="eye" size={15} /></button>
                            <button className="btn-icon" title="API keys" onClick={() => toggle(row.id, 'keys')}><Icon name="tag" size={15} /></button>
                            <button className="btn-icon" title="Managed accounts" onClick={() => toggle(row.id, 'accounts')}><Icon name="users" size={15} /></button>
                            <button className="btn-icon" title="Build jobs" onClick={() => toggle(row.id, 'jobs')}><Icon name="play" size={15} /></button>
                            <button className="btn-icon" title="Audit log" onClick={() => toggle(row.id, 'audit')}><Icon name="clock" size={15} /></button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="detail-row" key={row.id + '-panel'}>
                          <td colSpan={6}>
                            <div className="detail-panel">
                              {detailLoading && !d ? <p className="muted">Loading…</p> : d && (
                                <>
                                  {open.mode === 'overview' && <OverviewPanel d={d}
                                    dash={dashboards[`${row.id}:${dashDays}`]}
                                    days={dashDays}
                                    onDays={(n) => { setDashDays(n); if (!dashboards[`${row.id}:${n}`]) void loadDashboard(row.id, n); }}
                                    onMint={() => setMintFor(row.id)} onWebhook={() => setWebhookFor(row.id)}
                                    onOwner={() => setOwnerFor(row.id)} />}
                                  {open.mode === 'keys' && <KeysPanel d={d} onMint={() => setMintFor(row.id)}
                                    onRevoke={(k) => revokeKey(row.id, k)} />}
                                  {open.mode === 'accounts' && <AccountsPanel d={d} />}
                                  {open.mode === 'jobs' && <JobsPanel d={d} />}
                                  {open.mode === 'audit' && <AuditPanel d={d} />}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {createOpen && (
        <CreatePartnerModal
          onClose={() => setCreateOpen(false)}
          onCreate={async (form) => {
            try {
              const d = await backendApi.post<{ partner: { id: string; slug: string }; webhookSecret?: string }>(
                '/license/admin/partners', form);
              setCreateOpen(false);
              setNote(`Partner ${form.name} created.`);
              if (d.webhookSecret) {
                setSecret({
                  title: 'Webhook secret',
                  value: d.webhookSecret,
                  note: `Give this to ${form.name} for verifying inbound webhooks. Shown once.`,
                });
                setCopied(false);
              }
              load();
              setOpen({ id: d.partner.id, mode: 'overview' });
              void loadDetail(d.partner.id);
            } catch (err) { setError(friendlyError(err as ApiError)); }
          }}
        />
      )}

      {mintFor && (
        <MintKeyModal
          onClose={() => setMintFor(null)}
          onMint={async (name, scopes) => {
            try {
              const d = await backendApi.post<{ key: { raw: string; prefix: string } }>(
                `/license/admin/partners/${mintFor}/keys`, { name: name || null, scopes });
              const pid = mintFor;
              setMintFor(null);
              setSecret({
                title: 'API key',
                value: d.key.raw,
                note: 'Hand this to the partner over a secure channel. Shown once — it cannot be recovered, only revoked and re-minted.',
              });
              setCopied(false);
              void loadDetail(pid); void load();
            } catch (err) { setError(friendlyError(err as ApiError)); }
          }}
        />
      )}

      {webhookFor && (
        <WebhookModal
          current={details[webhookFor]?.overview.partner.webhook_url ?? ''}
          onClose={() => setWebhookFor(null)}
          onSave={async (url) => {
            try {
              const d = await backendApi.patch<{ cleared: boolean; webhookSecret?: string }>(
                `/license/admin/partners/${webhookFor}/webhook`, { webhookUrl: url || null });
              const pid = webhookFor;
              setWebhookFor(null);
              if (d.cleared) setNote('Webhook cleared.');
              if (d.webhookSecret) {
                setSecret({
                  title: 'Webhook secret (rotated)',
                  value: d.webhookSecret,
                  note: 'The previous secret is now INVALID — the partner must update their WPOS_WEBHOOK_SECRET. Shown once.',
                });
                setCopied(false);
              }
              void loadDetail(pid); void load();
            } catch (err) { setError(friendlyError(err as ApiError)); }
          }}
        />
      )}

      {ownerFor && (
        <OwnerModal
          current={String(details[ownerFor]?.overview.partner.owner_email ?? '')}
          onClose={() => setOwnerFor(null)}
          onSave={async (email) => {
            // Unlinking silently kills the owner's Partner dashboard + SSO —
            // confirm before removing an existing owner (mirrors revokeKey).
            const currentEmail = String(details[ownerFor]?.overview.partner.owner_email ?? '');
            if (!email && currentEmail && !window.confirm(
              `Unlink ${currentEmail}? They lose the Partner section and portal access immediately.`)) {
              return;
            }
            try {
              await backendApi.patch(`/license/admin/partners/${ownerFor}/owner`, { email: email || null });
              const pid = ownerFor;
              setOwnerFor(null);
              setNote(email
                ? `${email} now owns this partner — the Partner section appears on their next WPOS dashboard load.`
                : 'Owner unlinked.');
              void loadDetail(pid); void load();
            } catch (err) { setError(friendlyError(err as ApiError)); }
          }}
        />
      )}

      {secret && (
        <div className="modal-scrim" onClick={() => setSecret(null)}>
          <div className="modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-head">
              <h2 className="card-title" style={{ margin: 0 }}>{secret.title} — copy this now</h2>
              <button className="btn btn-sm btn-ghost" onClick={() => setSecret(null)}>Done</button>
            </div>
            <p className="muted">{secret.note}</p>
            <code className="mono" style={{ display: 'block', wordBreak: 'break-all', padding: 10, border: '1px solid var(--color-border-default)', borderRadius: 6 }}>
              {secret.value}
            </code>
            <div style={{ marginTop: 12 }}>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => navigator.clipboard.writeText(secret.value).then(() => setCopied(true))}
              >
                {copied ? 'Copied ✓' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Detail panels (Accounts-module style) ────────────────────────────────────

function OverviewPanel({ d, dash, days, onDays, onMint, onWebhook, onOwner }: {
  d: Detail; dash?: Dashboard; days: 7 | 30 | 90;
  onDays: (n: 7 | 30 | 90) => void; onMint: () => void; onWebhook: () => void; onOwner: () => void;
}) {
  const p = d.overview.partner;
  const lastJob = d.jobs[0];
  const activeKey = d.keys.find((k) => k.is_active);
  const t = dash?.totals;
  const wh = t?.webhook;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="detail-h" style={{ margin: 0 }}>Partner dashboard</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="seg">
            {([7, 30, 90] as const).map((n) => (
              <button key={n} className={`seg-btn${days === n ? ' on' : ''}`} onClick={() => onDays(n)}>{n}d</button>
            ))}
          </div>
          <button className="btn btn-sm" onClick={onOwner}>Owner…</button>
          <button className="btn btn-sm" onClick={onWebhook}>Webhook…</button>
          <button className="btn btn-sm btn-primary" onClick={onMint}>Mint Key</button>
        </div>
      </div>

      <div className="spark-row" style={{ marginBottom: 14 }}>
        <div className="card spark-card">
          <div className="spark-head"><span className="spark-title">Credits used ({days}d)</span>
            <span className="spark-total">{(t?.creditsAllTime ?? 0).toLocaleString()} all-time</span></div>
          <div className="statcard-value">{(t?.creditsWindow ?? 0).toLocaleString()}</div>
        </div>
        <div className="card spark-card">
          <div className="spark-head"><span className="spark-title">Build jobs ({days}d)</span>
            <span className="spark-total">{t?.jobs.successRate != null ? `${t.jobs.successRate}% success` : ''}</span></div>
          <div className="statcard-value">{t?.jobs.total ?? 0}
            {t && t.jobs.failed > 0 && <span className="badge badge-gray" style={{ marginLeft: 8, verticalAlign: 'middle' }}>{t.jobs.failed} failed</span>}
          </div>
        </div>
        <div className="card spark-card">
          <div className="spark-head"><span className="spark-title">Job spend ({days}d)</span>
            <span className="spark-total">{t?.jobs.credits ?? 0} credits</span></div>
          <div className="statcard-value">${Number(t?.jobs.cost ?? 0).toFixed(2)}</div>
        </div>
        <div className="card spark-card">
          <div className="spark-head"><span className="spark-title">Webhook deliveries ({days}d)</span>
            <span className="spark-total">{wh?.lastDeliveredAt ? `last ${ago(wh.lastDeliveredAt)}` : 'no deliveries'}</span></div>
          <div className="statcard-value">{wh?.delivered ?? 0}
            {wh && wh.failed > 0 && <span className="badge badge-gray" style={{ marginLeft: 8, verticalAlign: 'middle' }} title={wh.lastError ?? undefined}>{wh.failed} failed</span>}
            {wh && wh.pending > 0 && <span className="badge badge-amber" style={{ marginLeft: 8, verticalAlign: 'middle' }}>{wh.pending} pending</span>}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="chart-head">
          <h2 className="card-title" style={{ margin: 0 }}>Credit usage</h2>
          <div className="chart-head-right"><span className="chart-total">{(dash?.usageTotal ?? 0).toLocaleString()} credits in window</span></div>
        </div>
        {!dash ? <p className="muted">Loading…</p> : <UsageChart usage={dash.usage} />}
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="chart-head">
          <h2 className="card-title" style={{ margin: 0 }}>Growth (cumulative)</h2>
        </div>
        {!dash ? <p className="muted">Loading…</p> : <GrowthChart data={dash.growth} />}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))', gap: 14 }}>
        <div className="card" style={{ marginBottom: 0 }}>
          <h2 className="card-title">Integration health</h2>
          <dl className="detail-grid">
            <dt>Partner ID</dt><dd className="mono small">{p.id}</dd>
            <dt>Owner account</dt>
            <dd>{p.owner_email
              ? <>{String(p.owner_email)} <span className="muted small">— has the Partner section in their WPOS dashboard</span></>
              : <span className="muted">none — link a customer account to unlock their dashboard</span>}</dd>
            <dt>Default plan</dt><dd>{p.default_plan_id}</dd>
            <dt>Max accounts</dt><dd>{p.max_accounts ?? 'unlimited'}</dd>
            <dt>API access</dt>
            <dd>{activeKey
              ? <>{activeKey.name || activeKey.key_prefix} — last used {ago(t?.lastKeyUsedAt ?? activeKey.last_used_at)}</>
              : <span className="muted">no active key — mint one to enable the integration</span>}</dd>
            <dt>Webhook</dt>
            <dd>{p.webhook_url
              ? <span className="mono small" style={{ wordBreak: 'break-all' }}>{p.webhook_url}</span>
              : <span className="muted">none — events are not delivered anywhere</span>}</dd>
            <dt>Last build job</dt>
            <dd>{lastJob
              ? <>{ago(lastJob.created_at)} — <span className={statusBadge(lastJob.status)}>{lastJob.status}</span></>
              : <span className="muted">none yet</span>}</dd>
          </dl>
        </div>
        <div className="card" style={{ marginBottom: 0 }}>
          <h2 className="card-title">Top accounts by usage ({days}d)</h2>
          {!dash || dash.topAccounts.length === 0 ? (
            <p className="muted">No account usage yet — appears once the partner&apos;s customers start building.</p>
          ) : (
            <table className="table">
              <thead><tr><th>Account</th><th>Credits</th><th>Sites</th></tr></thead>
              <tbody>
                {dash.topAccounts.map((a) => (
                  <tr key={a.email}>
                    <td>
                      <div>{a.company ?? a.email}</div>
                      {a.company && <div className="muted small">{a.email}</div>}
                    </td>
                    <td>{a.credits.toLocaleString()}</td>
                    <td>{a.siteCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function KeysPanel({ d, onMint, onRevoke }: { d: Detail; onMint: () => void; onRevoke: (k: PartnerKey) => void }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div className="detail-h" style={{ margin: 0 }}>API keys</div>
        <button className="btn btn-sm btn-primary" onClick={onMint}>Mint Key</button>
      </div>
      {d.keys.length === 0 ? <p className="muted">No keys — mint one to let this partner call the API.</p> : (
        <table className="table">
          <thead><tr><th>Key</th><th>Scopes</th><th>Created</th><th>Last used</th><th>Status</th><th /></tr></thead>
          <tbody>
            {d.keys.map((k) => (
              <tr key={k.id}>
                <td>
                  <div className="mono">{k.key_prefix}…</div>
                  {k.name && <div className="muted small">{k.name}</div>}
                </td>
                <td>{k.scopes.map((sc) => <span key={sc} className="badge badge-purple" style={{ marginRight: 4 }}>{sc}</span>)}</td>
                <td className="muted">{new Date(k.created_at).toLocaleDateString()}</td>
                <td className="muted">{k.last_used_at ? ago(k.last_used_at) : 'never'}</td>
                <td><span className={k.is_active ? 'badge badge-green' : 'badge badge-gray'}>{k.is_active ? 'active' : 'revoked'}</span></td>
                <td>{k.is_active && <button className="btn btn-sm btn-danger" onClick={() => onRevoke(k)}>Revoke</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AccountsPanel({ d }: { d: Detail }) {
  return (
    <div>
      <div className="detail-h" style={{ marginTop: 0 }}>Managed accounts</div>
      {d.accounts.length === 0 ? (
        <p className="muted">No accounts yet — one is created each time the partner connects a customer workspace.</p>
      ) : (
        <>
          <table className="table">
            <thead><tr><th>Account</th><th>Plan</th><th>Status</th><th>Credits</th><th>Sites</th><th>Created</th></tr></thead>
            <tbody>
              {d.accounts.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div>{a.company ?? a.name ?? a.email}</div>
                    <div className="muted small">{a.email}</div>
                    {a.partner_external_ref && <div className="muted small mono">ref: {a.partner_external_ref}</div>}
                  </td>
                  <td>{a.plan_id}</td>
                  <td><span className={statusBadge(a.status)}>{a.status}</span></td>
                  <td>{a.credits_balance?.toLocaleString?.() ?? a.credits_balance}</td>
                  <td>{num(a.site_count)}</td>
                  <td className="muted">{new Date(a.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small" style={{ marginTop: 8 }}>
            Plans, credits and suspensions are managed in the <strong>Accounts</strong> module.
          </p>
        </>
      )}
    </div>
  );
}

function JobsPanel({ d }: { d: Detail }) {
  return (
    <div>
      <div className="detail-h" style={{ marginTop: 0 }}>Build jobs {d.jobsTotal > 0 && <span className="muted small">({d.jobsTotal} total)</span>}</div>
      {d.jobs.length === 0 ? (
        <p className="muted">No build jobs yet — they appear when the partner runs the WPOS agent on a customer site.</p>
      ) : (
        <table className="table">
          <thead><tr><th>Job</th><th>Site</th><th>Status</th><th>Model</th><th>Credits</th><th>Cost</th><th>Created</th><th>Duration</th></tr></thead>
          <tbody>
            {d.jobs.map((j) => {
              const secs = j.finished_at ? Math.round((+new Date(j.finished_at) - +new Date(j.created_at)) / 1000) : null;
              return (
                <tr key={j.id}>
                  <td className="mono small" title={j.error ?? undefined}>{j.id.slice(4, 12)}{j.error ? ' ⚠' : ''}</td>
                  <td className="mono small">{j.site_id?.slice(5, 13)}</td>
                  <td><span className={statusBadge(j.status)}>{j.status}</span></td>
                  <td>{j.model}</td>
                  <td>{j.credits_consumed ?? '—'}</td>
                  <td>{j.cost_usd != null ? `$${Number(j.cost_usd).toFixed(2)}` : '—'}</td>
                  <td className="muted">{new Date(j.created_at).toLocaleString()}</td>
                  <td className="muted">{secs != null ? `${Math.floor(secs / 60)}m ${secs % 60}s` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AuditPanel({ d }: { d: Detail }) {
  const audit = d.overview.auditLog ?? [];
  return (
    <div>
      <div className="detail-h" style={{ marginTop: 0 }}>Audit log <span className="muted small">(latest {audit.length})</span></div>
      {audit.length === 0 ? <p className="muted">No audit entries yet.</p> : (
        <table className="table">
          <thead><tr><th>Action</th><th>Target</th><th>Actor</th><th>When</th></tr></thead>
          <tbody>
            {audit.map((r, i) => (
              <tr key={i}>
                <td><span className="badge badge-gray mono">{r.action}</span></td>
                <td className="mono small">{r.target_id ?? ''}</td>
                <td>
                  <div>{auditActor(r)}</div>
                  {r.ip && <div className="muted small mono">{r.ip}</div>}
                </td>
                <td className="muted">{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CreatePartnerModal({ onClose, onCreate }: {
  onClose: () => void;
  onCreate: (form: { name: string; slug: string; defaultPlanId: string; maxAccounts: number | null; webhookUrl: string | null }) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [plan, setPlan] = useState('standard');
  const [maxAccounts, setMaxAccounts] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>New partner</h2>
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
        <form onSubmit={async (e) => {
          e.preventDefault();
          if (busy || !name.trim() || !slug.trim()) return;
          setBusy(true);
          await onCreate({
            name: name.trim(),
            slug: slug.trim(),
            defaultPlanId: plan,
            maxAccounts: maxAccounts ? Number(maxAccounts) : null,
            webhookUrl: webhookUrl.trim() || null,
          });
          setBusy(false);
        }}>
          <div className="form-row">
            <label className="field"><span>Name *</span>
              <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Amplus Agency AI" /></label>
            <label className="field"><span>Slug *</span>
              <input required value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} pattern="[a-z0-9-]+" placeholder="amplus" /></label>
          </div>
          <div className="form-row">
            <label className="field"><span>Default plan for provisioned accounts</span>
              <select value={plan} onChange={(e) => setPlan(e.target.value)}>
                {PLANS.map((pl) => <option key={pl}>{pl}</option>)}
              </select></label>
            <label className="field"><span>Max accounts (blank = unlimited)</span>
              <input type="number" min="1" value={maxAccounts} onChange={(e) => setMaxAccounts(e.target.value)} /></label>
          </div>
          <div className="form-row">
            <label className="field" style={{ flex: 1 }}><span>Webhook URL (optional, https — a secret is generated and shown once)</span>
              <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://partner.example.com/api/wpos/webhook" /></label>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !name.trim() || !slug.trim()}>
              {busy ? 'Creating…' : 'Create partner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MintKeyModal({ onClose, onMint }: {
  onClose: () => void;
  onMint: (name: string, scopes: string[]) => Promise<void>;
}) {
  const [name, setName] = useState('');
  // Scopes freeze at mint on the backend — default to everything so a partner
  // never needs a re-mint for a capability that shipped later.
  const [scopes, setScopes] = useState<string[]>([...ALL_SCOPES]);
  const [busy, setBusy] = useState(false);

  function toggle(scope: string) {
    setScopes((cur) => cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]);
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>Mint API key</h2>
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
        <form onSubmit={async (e) => { e.preventDefault(); if (!scopes.length || busy) return; setBusy(true); await onMint(name.trim(), scopes); setBusy(false); }}>
          <div className="form-row">
            <label className="field"><span>Key name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="production" /></label>
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <span>Scopes (frozen at mint — include everything the partner will ever need)</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6 }}>
              {ALL_SCOPES.map((sc) => (
                <label key={sc} style={{ display: 'flex', gap: 5, alignItems: 'center', fontWeight: 'normal' }}>
                  <input type="checkbox" checked={scopes.includes(sc)} onChange={() => toggle(sc)} /> {sc}
                </label>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-sm btn-primary" disabled={!scopes.length || busy}>
              {busy ? 'Minting…' : 'Mint key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function WebhookModal({ current, onClose, onSave }: {
  current: string;
  onClose: () => void;
  onSave: (url: string) => Promise<void>;
}) {
  const [url, setUrl] = useState(current);
  const [busy, setBusy] = useState(false);

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>Partner webhook</h2>
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
        <form onSubmit={async (e) => { e.preventDefault(); if (busy) return; setBusy(true); await onSave(url.trim()); setBusy(false); }}>
          <div className="form-row">
            <label className="field" style={{ flex: 1 }}>
              <span>Webhook URL (https). Saving generates a FRESH secret — the partner must update theirs. Leave blank to clear.</span>
              <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://partner.example.com/api/wpos/webhook" />
            </label>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save & rotate secret'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OwnerModal({ current, onClose, onSave }: {
  current: string; onClose: () => void; onSave: (email: string) => Promise<void>;
}) {
  const [email, setEmail] = useState(current);
  const [busy, setBusy] = useState(false);

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>Owner account</h2>
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
        <form onSubmit={async (e) => { e.preventDefault(); if (busy) return; setBusy(true); await onSave(email.trim()); setBusy(false); }}>
          <div className="form-row">
            <label className="field" style={{ flex: 1 }}>
              <span>
                Customer account email that OWNS this partner. Their WPOS dashboard
                unlocks a Partner section with SSO into the partner portal (full
                scopes, no API key needed). Leave blank to unlink.
              </span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@agency.com" />
            </label>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
              {busy ? 'Saving…' : email.trim() ? 'Link owner' : 'Unlink owner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
